import React, { useEffect, useState } from "react";
import TypingPanel from "./typing/TypingPanel";
import { fetchTypingConfig } from "../api";
import { track } from "../utils/analytics";
import IrabToolCta from "./IrabToolCta";

/**
 * PublicTypingPage — the anonymous-reachable Arabic typing tutor at /typing.
 *
 * Mounted from index.js BEFORE App, so none of the app's auth gates or URL
 * param handling ever run on this route (same contract as /irab and /sarf).
 * Anonymous visitors keep their progress in localStorage; a visitor with a
 * stored token gets server-side progress automatically (api.js attaches the
 * token). The static SEO page at muraja.app/tools/typing/ links here.
 *
 * English UI throughout — unlike /irab and /sarf, the audience is learners
 * who can't type (or comfortably read) Arabic yet.
 */
export default function PublicTypingPage() {
  const [enabled, setEnabled] = useState(null); // null = loading
  const hasToken = !!localStorage.getItem("token");

  useEffect(() => {
    document.title = "Learn to Type Arabic — Murājaʿa";
    fetchTypingConfig()
      .then((cfg) => setEnabled(!!cfg.enabled))
      .catch(() => setEnabled(true)); // fail open to the panel; server enforces
  }, []);

  return (
    <div className="dark-scope min-h-screen flex flex-col" style={{ background: "var(--ink)" }}>
      {/* Header */}
      <header className="flex items-center justify-between px-5 py-4 max-w-4xl w-full mx-auto">
        <a href="https://muraja.app" className="flex items-baseline gap-2 no-underline">
          <span className="font-amiri text-2xl text-dt-gold-bright">مراجعة</span>
          <span className="text-xs text-dt-parchment-dim tracking-wide">murājaʿa</span>
        </a>
        <a
          href="/"
          onClick={() => track("typing-signup-click", { context: "public", kind: "header" })}
          className="text-sm text-dt-parchment-dim hover:text-dt-gold-bright transition-colors"
        >
          {hasToken ? "Open the app →" : "Sign in →"}
        </a>
      </header>

      {/* Panel */}
      <main className="flex-1 flex items-start justify-center px-3 sm:px-4 pb-8">
        {enabled === false ? <ComingSoon /> : <TypingPanel context="public" />}
      </main>

      {/* Only alongside a working tool — pitching another tool next to a
          "coming soon" notice would be incoherent. */}
      {enabled !== false && <IrabToolCta context="typing-public" />}

      {/* Footer */}
      <footer className="px-5 py-4 text-center text-xs text-dt-parchment-dim space-x-3">
        <a href="https://muraja.app" className="hover:text-dt-parchment">muraja.app</a>
        <a href="https://muraja.app/tools/typing/" className="hover:text-dt-parchment">About this tool</a>
        <a href="https://muraja.app/privacy.html" className="hover:text-dt-parchment">Privacy</a>
        <a href="https://muraja.app/terms.html" className="hover:text-dt-parchment">Terms</a>
        <a href="https://muraja.app/ai-policy.html" className="hover:text-dt-parchment">AI Policy</a>
      </footer>
    </div>
  );
}

function ComingSoon() {
  return (
    <div className="surface-glass rounded-2xl shadow-2xl w-full max-w-lg px-6 py-10 text-center">
      <p className="font-cormorant font-semibold text-2xl text-dt-parchment mb-2">
        The Arabic typing tutor is almost ready
      </p>
      <p className="text-sm text-dt-parchment-dim mb-6">
        Graded lessons for the Arabic keyboard — where the letters live, then speed and
        accuracy. Start your free trial and be first to use it.
      </p>
      <a
        href="/?signup=1"
        onClick={() => track("typing-signup-click", { context: "public", kind: "coming-soon" })}
        className="inline-block px-5 py-2.5 rounded-xl bg-dt-gold text-dt-ink hover:brightness-105 text-sm font-semibold transition-[filter] no-underline"
      >
        Start your 7-day free trial
      </a>
    </div>
  );
}
